import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  OrganizationSubscription,
  CreateSubscriptionInput,
  UpdateSubscriptionInput,
} from "../types";

const SUBSCRIPTION_FIELDS = `
  id
  organizationId
  planId
  billingCycle
  amountPaid
  currency
  status
  autoRenew
  startDate
  endDate
  paymentReference
  remarks
  source
  createdAt
  updatedAt
`;

export const organizationSubscriptionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSubscriptions: builder.query<OrganizationSubscription[], void>({
      query: () => ({
        body: {
          query: `
            query Subscriptions {
              subscriptions {
                ${SUBSCRIPTION_FIELDS}
              }
            }
          `,
        },
      }),

      transformResponse: (res: unknown) =>
        pick<OrganizationSubscription[]>(res, "subscriptions"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((s) => ({
                type: "Subscription" as const,
                id: s.id,
              })),
              { type: "Subscription" as const, id: "LIST" },
            ]
          : [{ type: "Subscription" as const, id: "LIST" }],
    }),

    getSubscription: builder.query<OrganizationSubscription, string>({
      query: (id) => ({
        body: {
          query: `
            query Subscription($id: String!) {
              subscription(id: $id) {
                ${SUBSCRIPTION_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<OrganizationSubscription>(res, "subscription"),

      providesTags: (_result, _err, id) => [{ type: "Subscription", id }],
    }),

    getSubscriptionsByOrganization: builder.query<
      OrganizationSubscription[],
      string
    >({
      query: (organizationId) => ({
        body: {
          query: `
            query SubscriptionsByOrganization($organizationId: String!) {
              subscriptionsByOrganization(organizationId: $organizationId) {
                ${SUBSCRIPTION_FIELDS}
              }
            }
          `,
          variables: { organizationId },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<OrganizationSubscription[]>(res, "subscriptionsByOrganization"),

      providesTags: (result, _err, organizationId) =>
        result
          ? [
              ...result.map((s) => ({
                type: "Subscription" as const,
                id: s.id,
              })),
              { type: "Subscription" as const, id: `ORG-${organizationId}` },
            ]
          : [{ type: "Subscription" as const, id: `ORG-${organizationId}` }],
    }),

    createSubscription: builder.mutation<
      OrganizationSubscription,
      CreateSubscriptionInput
    >({
      query: (input) => ({
        body: {
          query: `
            mutation CreateSubscription($input: CreateSubscriptionInput!) {
              createSubscription(input: $input) {
                ${SUBSCRIPTION_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<OrganizationSubscription>(res, "createSubscription"),

      // A new "active" subscription also mirrors its plan/status onto the
      // Organization server-side (see organization-subscription/logic.ts), so
      // invalidate Organization too or the Organizations list keeps showing
      // the pre-subscription plan/status until an unrelated refetch happens.
      invalidatesTags: (result) => [
        { type: "Subscription", id: "LIST" },
        ...(result
          ? [
              {
                type: "Subscription" as const,
                id: `ORG-${result.organizationId}`,
              },
              { type: "Organization" as const, id: result.organizationId },
              { type: "Organization" as const, id: "LIST" },
            ]
          : []),
      ],
    }),

    updateSubscription: builder.mutation<
      OrganizationSubscription,
      { id: string; input: UpdateSubscriptionInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateSubscription(
              $id: String!
              $input: UpdateSubscriptionInput!
            ) {
              updateSubscription(id: $id, input: $input) {
                ${SUBSCRIPTION_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<OrganizationSubscription>(res, "updateSubscription"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Subscription", id },
        { type: "Subscription", id: "LIST" },
      ],
    }),

    activateSubscription: builder.mutation<OrganizationSubscription, string>({
      query: (id) => ({
        body: {
          query: `
            mutation ActivateSubscription($id: String!) {
              activateSubscription(id: $id) {
                ${SUBSCRIPTION_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<OrganizationSubscription>(res, "activateSubscription"),

      // Activating also mirrors plan/status onto the Organization — same
      // reasoning as createSubscription's invalidatesTags above.
      invalidatesTags: (result, _err, id) => [
        { type: "Subscription", id },
        { type: "Subscription", id: "LIST" },
        ...(result
          ? [
              { type: "Organization" as const, id: result.organizationId },
              { type: "Organization" as const, id: "LIST" },
            ]
          : []),
      ],
    }),

    cancelSubscription: builder.mutation<OrganizationSubscription, string>({
      query: (id) => ({
        body: {
          query: `
            mutation CancelSubscription($id: String!) {
              cancelSubscription(id: $id) {
                ${SUBSCRIPTION_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<OrganizationSubscription>(res, "cancelSubscription"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Subscription", id },
        { type: "Subscription", id: "LIST" },
      ],
    }),

    // No payment gateway — this persists a SubscriptionUpgradeRequest (visible to the
    // platform owner even if the best-effort email is missed/disabled) and
    // also emails every platform owner.
    requestSubscription: builder.mutation<
      boolean,
      {
        organizationId: string;
        planId: string;
        // Mainly meaningful for CUSTOM-code plans (no fixed price) — what
        // the owner is proposing to pay.
        proposedAmount?: number;
        proposedCurrency?: string;
        requestNote?: string;
      }
    >({
      query: ({ organizationId, planId, proposedAmount, proposedCurrency, requestNote }) => ({
        body: {
          query: `
            mutation RequestSubscription(
              $organizationId: String!
              $planId: String!
              $proposedAmount: Float
              $proposedCurrency: String
              $requestNote: String
            ) {
              requestSubscription(
                organizationId: $organizationId
                planId: $planId
                proposedAmount: $proposedAmount
                proposedCurrency: $proposedCurrency
                requestNote: $requestNote
              )
            }
          `,
          variables: {
            organizationId,
            planId,
            proposedAmount: proposedAmount ?? null,
            proposedCurrency: proposedCurrency ?? null,
            requestNote: requestNote ?? null,
          },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "requestSubscription"),

      invalidatesTags: (_result, _err, { organizationId }) => [
        { type: "SubscriptionUpgradeRequest", id: "LIST" },
        { type: "SubscriptionUpgradeRequest", id: `ORG-${organizationId}` },
      ],
    }),
  }),
});

export const {
  useGetSubscriptionsQuery,
  useLazyGetSubscriptionsQuery,
  useGetSubscriptionQuery,
  useLazyGetSubscriptionQuery,
  useGetSubscriptionsByOrganizationQuery,
  useLazyGetSubscriptionsByOrganizationQuery,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
  useActivateSubscriptionMutation,
  useCancelSubscriptionMutation,
  useRequestSubscriptionMutation,
} = organizationSubscriptionApi;
